<template>
  <master-layout pageTitle="Google RCS">
    <div>
      <div v-if="googlercsConnected" class="gridMargin">
        <div class="model-sections sectionPadding">
          <div class="flex-between header-container">
            <div class="flex-start alignCenter">
              <div class="channel-icon-wrap">
                <img src="../../../resources/images/Google_RCS_icon.svg" @error="handleImageError" class="channelImage" alt="Google RCS"/>
                <VerificationBadge
                  class="channel-verification-badge"
                  :verified="isChannelVerified('googlercs')"
                  :error-message="verificationErrorFor('googlercs')"
                />
              </div>
              <div>
                <h2 class="vtext-card-title">{{ googlercsSocialData.number || '--' }}</h2>
              </div>
            </div>
            <VButton class="vvd-btn-danger-outline" connotation="announcement" appearance="outlined" label="Disconnect" @click="() => disconnectPageMode = true"></VButton>
          </div>
          <div class="colMargin">
            <div class="flex-between vpb-10">
              <div class="flex-start">
                <p class="vtext-subtitle">Webhook</p>
                <HelpTooltip :iconStyle="{'padding-top': '3px'}">
                  A portion of the webhook has been masked for security purposes. If you need access to the full details, please raise a ticket to request them.
                </HelpTooltip>
              </div>
              <span class="vtext-body">{{ webhookToShow || '-' }}</span>
            </div>
            <p class="vtext-subtitle vpb-10">Private key</p>
            <div class="flex-between gap10P">
              <VTextArea v-model="updatedPrivateKey" aria-label="private key" placeholder="Enter Private key" appearance="ghost" class="vivid-textarea"></VTextArea>
              <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Update" :disabled="!updatedPrivateKey" style="height: 40px;" @click="updatePrivateKey"></VButton>
            </div>
          </div>
        </div>
        <div class="vcol vcol--no-padding vcol-xl-6 vcol-lg-6 vcol-md-6 vcol-sm-12 vcol-xs-12">
          <button class="cardContainer" type="button" @click="$router.push('/sales-channel/google-rcs/compliance-keywords')">
            <div class="cardContent">
              <img v-if="isDarkMode" src="../../../resources/images/compliance-keywords_dark.svg" width="40" class="imgIcons" alt="compliance"/>
              <img v-else src="../../../resources/images/compliance-keywords.svg" width="40" class="imgIcons" alt="compliance"/>
              <div>
                <p class="vtext-card-title vpb-5">Compliance keywords</p>
                <p class="vtext-subtitle">Set keywords that let users opt in or out receiving notifications.</p>
              </div>
            </div>
          </button>
        </div>
      </div>
      <div v-else class="connect-channel-container">
        <div class="cust_brdr sectionPadding">
          <p class="vtext-page-title">Connect Google RCS</p>
        </div>
        <!-- Step 1: Connection Method Selection -->
        <div v-if="currentStep === 'selection'" class="step-container">
          <div class="step-content">
            <div class="flex-center selection-container">
              <div>
                <h1 class="vtext-card-title text-center vpb-10">Select a connection method</h1>
                <p class="vtext-subtitle text-center">Select the option that best suits you.</p>
              </div>
            </div>
            <div class="sectionPadding">
              <div class="radio-card-group">
                <!-- Auto Option -->
                <div :class="['radio-card', { selected: selectedConnectionMethod === 'auto' }]" @click="selectedConnectionMethod = 'auto'">
                  <div class="radio-card-content">
                    <div>
                      <h3>Guided Setup</h3>
                      <p>Create and connect your Vonage application directly through Jumper using a simplified, step-by-step form. No need to switch to the Vonage API Dashboard to create the application.</p>
                    </div>
                    <VIcon :name="selectedConnectionMethod === 'auto' ? 'radio-checked-line' : 'radio-unchecked-line'" :connotation="selectedConnectionMethod === 'auto' ? 'cta' : undefined" class="radio-icon"></VIcon>
                  </div>
                </div>
                <!-- Manual Option -->
                <div :class="['radio-card', { selected: selectedConnectionMethod === 'manual' }]" @click="selectedConnectionMethod = 'manual'">
                  <div class="radio-card-content">
                    <div>
                      <h3>Manual Setup</h3>
                      <p>Use the <a href="https://dashboard.nexmo.com/applications" target="_blank" rel="noopener noreferrer" class="dashboard-link" @click.stop>Vonage API Dashboard</a> to create an application, then manually fill out the required details in the Jumper form to complete the connection.</p>
                    </div>
                    <VIcon :name="selectedConnectionMethod === 'manual' ? 'radio-checked-line' : 'radio-unchecked-line'" :connotation="selectedConnectionMethod === 'manual' ? 'cta' : undefined" class="radio-icon"></VIcon>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="sectionPadding navigation-section">
            <div class="navigation-buttons flex-between">
              <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" label="Next" @click="nextStep"></VButton>
            </div>
          </div>
        </div>

        <!-- Step 2: Auto Connection -->
        <div v-else-if="currentStep === 'auto'" class="step-container">
          <div class="step-content">
            <ConnectChannel 
              :channel-type="'google-rcs'"
              @go-back="handleGoBack"
            />
          </div>
        </div>

        <!-- Step 3: Manual Connection -->
        <div v-else-if="currentStep === 'manual'" class="step-container">
          <div class="step-content">
            <div class="margin20P">
              <div class="vmb-20">
                <div class="flex-start vpb-10">
                  <p class="vtext-subtitle">Agent ID<span class="important">*</span></p>
                  <HelpTooltip :iconStyle="{'padding-top': '3px'}">
                    <ul>
                      <li>Alphanumeric characters allowed(A–Z, a–z, and 0–9)</li>
                      <li>Underscores (_) and hyphens (-) are allowed</li>
                      <li>No spaces or other special characters</li>
                      <li>Max: 50 characters</li>
                    </ul>
                  </HelpTooltip>
                </div>
                <div class="counter-wrapper">
                  <VTextField v-model="account.agentname" placeholder="Enter Agent ID (Sender ID)" :maxlength="50" aria-label="Agent name" appearance="ghost" class="vivid-input"></VTextField>
                  <div class="flex-between flex100P">
                    <div class="flex100P">
                      <div v-if="account.agentname && !isAgentNameValid" class="validation-error">
                        Agent name can only contain letters, numbers, underscores (_), and hyphens (-). No spaces or other special characters allowed.
                      </div>
                    </div>
                    <div class="custom-counter">
                      {{ (account.agentname || '').length }}/50
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="rcs-container">
              <div class="vrow vmb-20">
                <div class="vcol vcol--no-padding vcol-xl-6 vcol-lg-6 vcol-md-6 vcol-sm-12 vcol-xs-12 colMargin custPaddingRight">
                  <p class="vtext-subtitle vpb-10">App ID<span class="important">*</span></p>
                  <VTextField v-model="account.appid" placeholder="Enter App ID" aria-label="App ID" appearance="ghost" class="vivid-input"></VTextField>
                </div>
                <div class="vcol vcol--no-padding vcol-xl-6 vcol-lg-6 vcol-md-6 vcol-sm-12 vcol-xs-12 colMargin">
                  <p class="vtext-subtitle vpb-10">App name<span class="important">*</span></p>
                  <VTextField v-model="account.appname" placeholder="Enter App name" aria-label="App name" appearance="ghost" class="vivid-input"></VTextField>
                </div>
              </div>
              <div class="vrow vmb-20">
                <div class="vcol vcol--no-padding vcol-xl-6 vcol-lg-6 vcol-md-6 vcol-sm-12 vcol-xs-12 colMargin custPaddingRight">
                  <p class="vtext-subtitle vpb-10">API key<span class="important">*</span></p>
                  <VTextField v-model="account.apikey" placeholder="Enter API key" aria-label="API key" appearance="ghost" class="vivid-input"></VTextField>
                </div>
                <div class="vcol vcol--no-padding vcol-xl-6 vcol-lg-6 vcol-md-6 vcol-sm-12 vcol-xs-12 colMargin">
                  <p class="vtext-subtitle vpb-10">API secret<span class="important">*</span></p>
                  <VTextField v-model="account.apisecret" placeholder="Enter API secret" aria-label="API secret" appearance="ghost" class="vivid-input"></VTextField>
                </div>
              </div>
              <div class="vmb-20">
                <p class="vtext-subtitle vpb-10">Private key <span class="important">*</span></p>
                <VTextArea v-model="account.privatekey" aria-label="private key" placeholder="Enter Private key" appearance="ghost" class="vivid-textarea"></VTextArea>
              </div>
            </div>
          </div>
          <div class="sectionPadding navigation-section">
            <div class="navigation-buttons flex-between">
              <VButton class="vvd-btn-cancel flex100P" appearance="filled" label="Previous" @click="previousStep"></VButton>
              <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" :label="spinnerLoading ? 'Submitting...' : 'Submit'" :disabled="isDisabled || spinnerLoading" @click="connectgooglercs"></VButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Disconnect Confirmation Dialog -->
    <VDialog :open="disconnectPageMode" headline="Are you sure?" modal @close="disconnectPageMode = false">
      <template #body>
        <p class="vtext-body">You want to disconnect your Google RCS account?</p>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" @click="disconnectPageMode = false"></VButton>
        <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Yes" @click="disconnectGooglercs"></VButton>
      </template>
    </VDialog>

    <!-- Reconnect Confirmation Dialog -->
    <VDialog :open="showReconnectDialog" headline="Reconnect Account" modal @close="showReconnectDialog = false">
      <template #body>
        <p class="vtext-body">This appears to be previously disconnected account, connecting this will show the data associated with that connection. Do you want to proceed?</p>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-cancel" appearance="filled" label="No" @click="handleReconnectNo"></VButton>
        <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Yes" @click="reconnectGoogleRCS"></VButton>
      </template>
    </VDialog>
  </master-layout>
</template>
  
<script>
import apiInterceptor from '@/shared/apiInterceptor';
import HelpTooltip from '@/components/HelpTooltip.vue';
import ConnectChannel from '@/pages/sales-channel/connect-channel.vue';
import VerificationBadge from '@/components/VerificationBadge.vue';
import { useUIStore } from '@/stores/ui';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { useConnectionVerification } from '@/composables/useConnectionVerification';
import { environment } from '@/shared/env';
import { useAuthStore } from '@/stores/auth';

export default {
  components: { 
    HelpTooltip,
    ConnectChannel,
    VerificationBadge
  },
  setup() {
    const { googlercsConnected, googlercsSocialData } = useSocialChannels();
    // Verify only this channel via ?channels=googlercs
    const { isChannelVerified, verificationErrorFor, refreshChannel, invalidateChannel } = useConnectionVerification(['googlercs']);
    return { googlercsConnected, googlercsSocialData, isChannelVerified, verificationErrorFor, refreshChannel, invalidateChannel };
  },
  data() {
    return {
      spinnerLoading: false,
      account: {
        agentname: '',
        apisecret: '',
        appid: '',
        appname: '',
        apikey: '',
        privatekey: '',
      },
      disconnectPageMode: false,
      showReconnectDialog: false,
      updatedPrivateKey: '',
      selectedConnectionMethod: 'auto',
      currentStep: 'selection'
    }
  },
  computed: {
    isDarkMode() { return useUIStore().isDarkMode; },
    isDisabled() {
      return !this.account.agentname || !this.account.apisecret || !this.account.apikey || !this.account.appid || !this.account.appname || !this.account.privatekey || !this.isAgentNameValid;
    },
    webhookToShow() {
      if (this.googlercsConnected) {
        const url = new URL(this.googlercsSocialData.webhook);
        url.hostname = 'XXXXXXXXXX';
        return url.href;
      }
    },
    isAgentNameValid() {
      // Allow letters, numbers, underscores, and hyphens
      return /^[A-Za-z0-9_-]+$/.test(this.account.agentname);
    }
  },
  methods: {
    async connectgooglercs() {
      try {
        this.spinnerLoading = true;
        if (this.isDisabled) {
          return;
        }
        const reqBody = {
          apikey: this.account.apikey,
          appid: this.account.appid,
          appname: this.account.appname,
          privatekey: this.account.privatekey,
          apisecret: this.account.apisecret,
          account_name: this.account.agentname,
          active: true,
          confirm: false
        };
        const response = await apiInterceptor.post(environment("VUE_APP_API_URL") + "social/v1/google-rcs", reqBody);
        
        if (response.data === null && response.dialogMessage) {
          this.spinnerLoading = false;
          this.showReconnectDialog = true;
          return;
        }
        
        useUIStore().updateToast({
          open: true,
          message: "Your Google RCS channel is connected successfully!",
          type: "success"
        });
        // Dispatch custom event to refresh LiveChatHome customers
        const refreshEvent = new CustomEvent("refresh-customers", {
          detail: { 
            action: "connect",
            channel: "google-rcs",
            timestamp: new Date().toISOString()
          }
        });
        document.dispatchEvent(refreshEvent);
        
        // Re-fetch the connections list and mirror it into the auth store.
        await useAuthStore().refreshSocialConnections();
        // Credentials just changed — re-check the verification status.
        await this.refreshChannel('googlercs');
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message,
          type: "danger"
        });
      } finally {
        this.spinnerLoading = false;
      }
    },
    async disconnectGooglercs() {
      try {
        this.disconnectPageMode = false;
        useUIStore().updateLoading({ isLoading: true, message: 'Disconnecting Google RCS account...' });
        await apiInterceptor.delete(environment("VUE_APP_API_URL") + "social/v1/google-rcs");
        useUIStore().updateToast({ open: true, message: "Your Google RCS Account has been disconnected successfully", type: "success" });
        // Disconnected channels are never verified — clear the status locally.
        this.invalidateChannel('googlercs');
        this.resetData();
        // Re-fetch the connections list and mirror it into the auth store, instead of
        // going through checkToken()'s ensureQueryData, which returns existing cached
        // data without re-fetching when staleTime is Infinity — leaving the store
        // showing Google RCS as still connected.
        await useAuthStore().refreshSocialConnections();
        
        // Dispatch custom event to refresh LiveChatHome customers
        const refreshEvent = new CustomEvent("refresh-customers", {
          detail: { 
            action: "disconnect",
            channel: "google-rcs",
            timestamp: new Date().toISOString()
          }
        });
        document.dispatchEvent(refreshEvent);
        
        this.$router.replace({ path: '/sales-channel/google-rcs' });
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error.message || "Something went wrong", type: "danger" });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    resetData() {
      this.account = {
        agentname: '',
        apisecret: '',
        appid: '',
        appname: '',
        apikey: '',
        privatekey: '',
      };
    },
    async updatePrivateKey() {
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Updating private key...' });
        if (!this.updatedPrivateKey) {
          return useUIStore().updateToast({
            open: true,
            message: "Private key cannot be empty",
            type: "danger"
          });
        }
        const reqBody = {
          privatekey: this.updatedPrivateKey,
        };
        await apiInterceptor.patch(environment("VUE_APP_API_URL") + `social/v1/google-rcs/${this.googlercsSocialData.id}`, reqBody);
        useUIStore().updateToast({
          open: true,
          message: "Private key updated successfully!",
          type: "success"
        });
        // Re-fetch the connections list and mirror it into the auth store.
        await useAuthStore().refreshSocialConnections();
        // Credentials just changed — re-check the verification status.
        await this.refreshChannel('googlercs');
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: "Failed to update key",
          type: "danger"
        });
      } finally {
        this.updatedPrivateKey = '';
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    nextStep() {
      if (this.selectedConnectionMethod === 'auto') {
        this.currentStep = 'auto';
      } else if (this.selectedConnectionMethod === 'manual') {
        this.currentStep = 'manual';
      }
    },
    previousStep() {
      this.currentStep = 'selection';
      this.resetData();
    },
    handleGoBack() {
      this.currentStep = 'selection';
      this.resetData();
    },
    handleReconnectNo() {
      this.showReconnectDialog = false;
      this.previousStep();
    },
    async reconnectGoogleRCS() {
      try {
        this.showReconnectDialog = false;
        this.spinnerLoading = true;
        const reqBody = {
          apikey: this.account.apikey,
          appid: this.account.appid,
          appname: this.account.appname,
          privatekey: this.account.privatekey,
          apisecret: this.account.apisecret,
          account_name: this.account.agentname,
          active: true,
          confirm: true
        };
        await apiInterceptor.post(environment("VUE_APP_API_URL") + "social/v1/google-rcs", reqBody);
        
        useUIStore().updateToast({
          open: true,
          message: "Your Google RCS channel is connected successfully!",
          type: "success"
        });
        
        const refreshEvent = new CustomEvent("refresh-customers", {
          detail: { 
            action: "connect",
            channel: "google-rcs",
            timestamp: new Date().toISOString()
          }
        });
        document.dispatchEvent(refreshEvent);
        
        // Re-fetch the connections list and mirror it into the auth store.
        await useAuthStore().refreshSocialConnections();
        // Credentials just changed — re-check the verification status.
        await this.refreshChannel('googlercs');
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Failed to reconnect account",
          type: "danger"
        });
      } finally {
        this.spinnerLoading = false;
      }
    }
  }
}
</script>
  
<style scoped>
.model-sections {
  background: var(--white);
  border-radius: 5px;
}
.channelImage {
  width: 50px;
  height: auto;
}
.channel-icon-wrap {
  position: relative;
  display: inline-block;
  line-height: 0;
}
.channel-verification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
}
.header-container {
  border-bottom: 1px solid var(--background-primary);
  margin-bottom: 10px;
  padding-bottom: 10px;
}
.colMargin {
  margin-bottom: 10px;
}
.cardContainer {
  min-height: 90px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: var(--white);
  border: 1px solid var(--white);
  box-shadow: none;
  margin: 20px 0px 0px 0px;
  width: 100%;
  padding: 15px 20px;
  text-align: left;
  font: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.cardContainer:hover {
  border-color: var(--purple);
}
.cardContainer:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}
.cardContent {
  display: flex;
  align-items: center;
  width: 100%;
}
.imgIcons {
  margin-right: 20px;
  flex-shrink: 0;
}
.vivid-textarea {
  background: var(--background-primary);
  border-radius: 5px;
  width: 100%;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.connect-channel-container {
  max-width: 950px;
  border-radius: 5px;
  margin: 20px auto 30px auto;
  min-height: calc(100dvh - 120px);
  height: calc(100% - 30px);
  background: var(--white);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}
.step-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}
.step-content {
  flex: 1;
}
.navigation-section {
  margin-top: auto;
}
.navigation-buttons {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}
.selection-container {
  padding: 20px 0px;
  border-bottom: 1px solid var(--background-primary);
}
.radio-card-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
}
.radio-card {
  flex: 1 1 300px;
  border: 1px solid var(--background-primary);
  border-radius: 12px;
  padding: 1rem;
  transition: border-color 0.3s ease;
  background: var(--white);
  cursor: pointer;
}
.radio-card.selected {
  border-color: var(--purple);
}
.radio-card h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--black);
}
.radio-card p {
  font-size: 14px;
  font-weight: 400;
  color: var(--gray-04);
  margin: 0.5rem 0 0;
}
.radio-card-content {
  display: flex;
  align-items: center;
  gap: 25px;
}
.dashboard-link {
  color: var(--purple);
  text-decoration: underline;
  font-weight: 400;
}
.dashboard-link:hover {
  color: var(--purple);
  opacity: 0.8;
}
.validation-error {
  color: red;
  font-size: 12px;
}
.custPaddingRight {
  padding-right: 20px;
}
.margin20P{
  margin: 20px;
}
.rcs-container {
  margin: 25px 30px;
}
@media only screen and (max-width: 760px) {
  .custPaddingRight {
    padding-right: 0px;
  }
  .cardContainer {
    margin: 10px 0;
  }
  .navigation-buttons {
    flex-direction: column;
    gap: 12px;
  }
}
</style>